import { useEffect, useRef, useState } from 'react';
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import DeleteLine from '@/assets/icons/delete_line.svg?component';
import AddLine from '@/assets/icons/ri_add_line.svg?component';
import { Alert } from '@/components/atoms/Alert';
import { Button } from '@/components/atoms/Buttons';
import { Overline } from '@/components/atoms/Eyebrow';
import { ModalComponent } from '@/components/molecules/ModalComponent';
import { ToastInfo } from '@/components/molecules/ToastTemplate/ToastTemplate';
import {
    AllocationSummary,
    ChoiceCards,
    ConfirmRegistration,
    computeAllocationSummary,
    defaultOperationDate,
    FormNumberField,
    FormSelect,
    FormTextField,
    parseAllocationCount,
    SuggestNameButton,
    useServerErrorMessage,
} from '@/components/organisms/FishGroupForms';
import { usePermissions } from '@/contexts/auth';
import { type TimelineFishGroup, useSiteFishGroupActions } from '@/domain/fishGroups';

import { earliestEventDay, eventTimestamp } from '../../helpers/siteActions/actionDate';
import { splitBioscopeOutcomes } from '../../helpers/siteActions/bioscopeOutcomes';
import {
    currentStay,
    movableBioscopeIds,
    stayBioscopeIds,
} from '../../helpers/siteActions/groupFacts';
import { splitConfirmItems } from '../../helpers/siteActions/operationConfirm';
import type { SitePen } from '../../helpers/siteActions/sitePens';
import {
    defaultSplitRows,
    type SplitRowValues,
    splitBioscopeOptions,
    splitPenOptions,
    suggestRowName,
} from '../../helpers/siteActions/splitRows';
import Glyph from '../Glyph/Glyph';
import ModalFormActions from '../ModalFormActions/ModalFormActions';
import ActionDialogTitle from '../SiteActionDialog/ActionDialogTitle';
import EventDateField from '../SiteActionDialog/EventDateField';
import GroupSummaryRow from '../SiteActionDialog/GroupSummaryRow';
import PensStatus, { pensKnown, type SitePensState } from '../SiteActionDialog/PensStatus';

import dialogStyles from '../SiteActionDialog/SiteActionDialog.module.scss';
import styles from './SplitFishGroupModal.module.scss';

interface SplitFormValues {
    date: Date | null;
    rows: SplitRowValues[];
    /** 'keep', or the cage id the bioscopes move to. */
    bioscope: string;
}

interface SplitFishGroupModalProps {
    clientId: number;
    locationId: number;
    /** Real names: the dialog never mounts in demo mode. */
    group: TimelineFishGroup;
    pens: readonly SitePen[];
    pensState: SitePensState;
    /** The season segment of the group's stocking, for Suggest. */
    season: string | null;
    /** Real names of the site's groups, which Suggest won't repeat. */
    siteNames: readonly string[];
    /** A grading: the same split, labelled `grading` (Q15: no size classes yet). */
    grading?: boolean;
    onClose: () => void;
    onDone: () => void;
}

/**
 * One group into several pens (Figma 663:89494), or a grading (664:89791): the group closes and
 * each row becomes a new group; a row in its own pen is the continuation. Confirmed first.
 */
export default function SplitFishGroupModal(props: SplitFishGroupModalProps) {
    const { clientId, locationId, group, pens, pensState, season, siteNames, onClose, onDone } =
        props;
    const { grading = false } = props;
    const { t } = useTranslation('fishGroups');
    const kind = grading ? 'grading' : 'split';
    const { canMoveBioscopes } = usePermissions();
    const [confirming, setConfirming] = useState<SplitFormValues | null>(null);
    const actions = useSiteFishGroupActions(clientId, locationId);
    const serverError = useServerErrorMessage(actions.state.error);

    const stay = currentStay(group);
    const currentCageId = stay?.cageId ?? null;
    // Measuring now, for the heading; which can move depends on the split's date.
    const bioscopeIds = stayBioscopeIds(stay);
    const movableAt = (day: Date | null) =>
        movableBioscopeIds(
            stay,
            day ? new Date(eventTimestamp(day, stay?.from ?? null)) : new Date()
        );
    const earliest = earliestEventDay(stay?.from ?? null);
    // Customers never create a group outside a stocking (Mauro, 2026-10-20).
    const inNoStocking = group.stockingCycleId === null;
    const penOf = (cageId: string) => pens.find((pen) => String(pen.cageId) === cageId);

    const { handleSubmit, control, setValue, getValues, getFieldState, formState } =
        useForm<SplitFormValues>({
            defaultValues: {
                date: defaultOperationDate(),
                rows: defaultSplitRows(pens, currentCageId, season, siteNames),
                bioscope: 'keep',
            },
        });
    const { fields, append, remove, replace } = useFieldArray({ control, name: 'rows' });
    // Pens that arrive after the dialog opened seed the rows then, unless they were edited.
    const seeded = useRef(pens.length > 0);
    useEffect(() => {
        if (seeded.current || pens.length === 0) return;
        seeded.current = true;
        if (!getFieldState('rows').isDirty) {
            replace(defaultSplitRows(pens, currentCageId, season, siteNames));
        }
    }, [pens, currentCageId, season, siteNames, getFieldState, replace]);
    const rows = useWatch({ control, name: 'rows' });
    // A bioscope booked into the pen after the split's date isn't there to move yet.
    const movableIds = movableAt(useWatch({ control, name: 'date' }));
    const mayMoveBioscopes = movableIds.length > 0 && canMoveBioscopes(clientId, locationId);
    const submitting = formState.isSubmitting || actions.state.isLoading;

    const summary = computeAllocationSummary({
        sources: [{ fishGroupId: group.id }],
        destinations: rows.map((row) => ({ estimatedFishCount: row.fishCount })),
        populationById: new Map([
            [group.id, { fishGroupId: group.id, initialPopulationSize: group.estimatedCount }],
        ]),
    });
    const rowCageIds = rows.map((row) => Number(row.cageId)).filter((id) => id > 0);
    const bioscopeOptions = splitBioscopeOptions(pens, rowCageIds, currentCageId, movableIds, t);
    const bioscope = useWatch({ control, name: 'bioscope' });
    // A row whose pen changed, or that was removed, takes its bioscope choice with it.
    const bioscopeOffered = bioscopeOptions.some((option) => option.value === bioscope);
    useEffect(() => {
        if (!bioscopeOffered) setValue('bioscope', 'keep');
    }, [bioscopeOffered, setValue]);
    const takenNames = (except: number) => [
        ...siteNames,
        ...rows.filter((_, index) => index !== except).map((row) => row.name),
    ];

    const close = () => {
        if (submitting) return;
        actions.state.reset();
        onClose();
    };

    const register = async (values: SplitFormValues) => {
        if (!values.date) return;
        const destinationIds = values.rows.map((row) => Number(row.cageId));
        const picked = values.bioscope === 'keep' ? null : Number(values.bioscope);
        // Only into a pen the fish go to: the backend refuses any other target.
        const target =
            picked !== null && picked !== currentCageId && destinationIds.includes(picked)
                ? picked
                : null;
        const done = await actions
            .split({
                sourceId: group.id,
                date: eventTimestamp(values.date, stay?.from ?? null),
                grading,
                destinations: values.rows.map((row) => ({
                    name: row.name,
                    cageId: Number(row.cageId),
                    fishCount: parseAllocationCount(row.fishCount),
                })),
                bioscopeMoves:
                    target !== null && mayMoveBioscopes
                        ? movableAt(values.date).map((bioscopeId) => ({
                              bioscopeId,
                              targetCageId: target,
                          }))
                        : [],
            })
            .then(
                () => true,
                () => false
            );
        if (!done) return;
        ToastInfo({
            content: t(`siteActions.${kind}.successToast`, {
                name: group.name,
                count: values.rows.length,
            }),
            status: 'success',
            icon: 'success',
            options: { toastId: `site-fish-group-${kind}-success`, autoClose: 5000 },
        });
        actions.state.reset();
        onDone();
    };

    const header = (
        <ActionDialogTitle
            kind={kind}
            title={t(`siteActions.${kind}.title`)}
            groupName={group.name}
            penName={stay?.cageName ?? null}
            bioscopeIds={bioscopeIds}
        />
    );

    if (confirming?.date) {
        const fromPen = stay?.cageName ?? '';
        const toPens = confirming.rows.map((row) => penOf(row.cageId)?.displayName ?? '');
        const destinationIds = confirming.rows.map((row) => Number(row.cageId));
        const picked = confirming.bioscope === 'keep' ? null : Number(confirming.bioscope);
        // The same target the request sends: a pen the fish go to, other than the group's own.
        const moveTo =
            mayMoveBioscopes &&
            picked !== null &&
            picked !== currentCageId &&
            destinationIds.includes(picked)
                ? picked
                : null;
        return (
            <ModalComponent
                isOpen
                size="lg"
                onClose={close}
                aria-label={t('siteActions.confirm.title')}
                backdrop="static"
                body={
                    <ConfirmRegistration
                        fromPens={[fromPen]}
                        toPens={toPens}
                        title={t('siteActions.confirm.title')}
                        items={splitConfirmItems(
                            {
                                kind,
                                date: confirming.date,
                                groupName: group.name,
                                newGroups: confirming.rows.length,
                                bioscopes: splitBioscopeOutcomes({
                                    pens,
                                    sourceCageId: currentCageId,
                                    destinationCageIds: destinationIds,
                                    moving:
                                        moveTo !== null
                                            ? { ids: movableAt(confirming.date), toCageId: moveTo }
                                            : null,
                                }),
                                emptiedPen:
                                    currentCageId !== null &&
                                    !destinationIds.includes(currentCageId)
                                        ? fromPen
                                        : null,
                            },
                            t
                        )}
                        error={
                            serverError && <p className={dialogStyles.serverError}>{serverError}</p>
                        }
                        cancelLabel={t('actions.cancel')}
                        confirmLabel={t('siteActions.confirm.confirm')}
                        isSubmitting={submitting}
                        onCancel={() => {
                            actions.state.reset();
                            setConfirming(null);
                        }}
                        onConfirm={() => register(confirming)}
                    />
                }
            />
        );
    }

    const penOptions = splitPenOptions(pens, currentCageId, t);
    const body = (
        <form
            className={dialogStyles.form}
            onSubmit={handleSubmit((values) => setConfirming(values))}>
            <EventDateField control={control} name="date" earliest={earliest} />
            {/* The grading design shows no pool; a split's rows are allocated from it. */}
            {!grading && (
                <AllocationSummary
                    summary={summary}
                    sources={[{ fishGroupId: group.id, name: group.name }]}
                    populationById={new Map()}
                    titleKey="siteActions.split.pool"
                    disclaimerKey="siteActions.split.poolBasis"
                />
            )}
            <section className={dialogStyles.section}>
                <Overline as="h3" className={dialogStyles.sectionLabel}>
                    {t(`siteActions.${kind}.groupToSplit`)}
                </Overline>
                <GroupSummaryRow group={group} />
            </section>
            <section className={dialogStyles.section}>
                <Overline as="h3" className={dialogStyles.sectionLabel}>
                    {t(`siteActions.${kind}.newGroups`)}
                </Overline>
                <PensStatus state={pensState} />
                {fields.map((field, index) => (
                    <div key={field.id} className={styles.row}>
                        <FormTextField
                            control={control}
                            name={`rows.${index}.name`}
                            label={t('siteActions.split.name')}
                            rules={{
                                validate: (value) =>
                                    typeof value === 'string' && value.trim() !== '',
                            }}
                            labelAction={
                                <SuggestNameButton
                                    cageName={penOf(rows[index]?.cageId ?? '')?.cageName}
                                    operation="split"
                                    season={season}
                                    existingNames={takenNames(index)}
                                    onApply={(name) =>
                                        setValue(`rows.${index}.name`, name, {
                                            shouldValidate: true,
                                        })
                                    }
                                />
                            }
                        />
                        <FormSelect
                            control={control}
                            name={`rows.${index}.cageId`}
                            label={t('siteActions.split.pen')}
                            placeholder={t('siteActions.split.penPlaceholder')}
                            options={penOptions}
                            rules={{
                                required: true,
                                validate: (value) =>
                                    getValues('rows').filter((row) => row.cageId === value)
                                        .length === 1 || t('siteActions.split.penTwice'),
                            }}
                        />
                        <FormNumberField
                            control={control}
                            name={`rows.${index}.fishCount`}
                            label={t('siteActions.split.fishCount')}
                            min={1}
                            step={1}
                            rules={{
                                validate: (value) =>
                                    parseAllocationCount(String(value)) !== null ||
                                    t('siteActions.split.countRequired'),
                            }}
                        />
                        <Button
                            variant="danger"
                            iconOnly
                            aria-label={t('siteActions.split.removeRow', { index: index + 1 })}
                            icon={<Glyph icon={DeleteLine} />}
                            className={styles.remove}
                            disabled={fields.length <= 2}
                            onClick={() => remove(index)}
                        />
                    </div>
                ))}
                <div>
                    <Button
                        variant="secondary"
                        size="small"
                        icon={<Glyph icon={AddLine} />}
                        disabled={fields.length >= penOptions.length}
                        onClick={() => {
                            const used = new Set(rows.map((row) => row.cageId));
                            const pen = pens.find(
                                (candidate) =>
                                    candidate.occupant === null &&
                                    !used.has(String(candidate.cageId))
                            );
                            append({
                                name: suggestRowName(pen, season, takenNames(-1)),
                                cageId: pen ? String(pen.cageId) : '',
                                fishCount: '',
                            });
                        }}>
                        {t('siteActions.split.addPen')}
                    </Button>
                </div>
            </section>
            {mayMoveBioscopes && (
                <section className={dialogStyles.section}>
                    <Overline as="h3" className={dialogStyles.sectionLabel}>
                        {t('siteActions.form.bioscope')}
                    </Overline>
                    <Controller
                        control={control}
                        name="bioscope"
                        render={({ field }) => (
                            <ChoiceCards
                                aria-label={t('siteActions.form.bioscope')}
                                value={field.value}
                                onChange={field.onChange}
                                options={bioscopeOptions}
                            />
                        )}
                    />
                </section>
            )}
            {inNoStocking && (
                <Alert tone="warning" size="small">
                    {t('siteActions.split.needsStocking')}
                </Alert>
            )}
            {serverError && <p className={dialogStyles.serverError}>{serverError}</p>}
            <ModalFormActions
                cancelLabel={t('actions.cancel')}
                submitLabel={t(`siteActions.${kind}.submit`)}
                isSubmitting={submitting}
                submitDisabled={inNoStocking || !pensKnown(pensState)}
                onCancel={close}
            />
        </form>
    );

    return (
        <ModalComponent
            isOpen
            size="lg"
            onClose={close}
            title={header}
            body={body}
            backdrop="static"
        />
    );
}
